import { getMediaConfig } from '@/config/media-upload';
import { MediaDimensions } from '@/lib/types/media/core';
import { MediaPlacement } from '@prisma/client';

interface ValidationResult {
  isValid: boolean;
  error?: string;
  metadata?: {
    dimensions?: MediaDimensions;
  };
}

export async function validateMediaFile(file: File, placement: MediaPlacement): Promise<ValidationResult> {
  const config = getMediaConfig(placement);

  // Check file size
  if (file.size > config.maxSize) {
    return {
      isValid: false,
      error: `File size exceeds maximum allowed size of ${config.maxSize} bytes`
    };
  }

  // Check file type
  const isValidType = Object.keys(config.acceptedTypes).some(type => {
    const pattern = new RegExp(type.replace('*', '.*'));
    return pattern.test(file.type);
  });

  if (!isValidType) {
    return {
      isValid: false,
      error: `Invalid file type. Accepted types: ${Object.keys(config.acceptedTypes).join(', ')}`
    };
  }

  // For images, check dimensions
  if (file.type.startsWith('image/') && config.dimensions) {
    try {
      const dimensions = await getImageDimensions(file);

      if (config.dimensions.minWidth && dimensions.width < config.dimensions.minWidth) {
        return {
          isValid: false,
          error: `Image width must be at least ${config.dimensions.minWidth}px`
        };
      }

      if (config.dimensions.minHeight && dimensions.height < config.dimensions.minHeight) {
        return {
          isValid: false,
          error: `Image height must be at least ${config.dimensions.minHeight}px`
        };
      }

      if (config.dimensions.maxWidth && dimensions.width > config.dimensions.maxWidth) {
        return {
          isValid: false,
          error: `Image width must not exceed ${config.dimensions.maxWidth}px`
        };
      }

      if (config.dimensions.maxHeight && dimensions.height > config.dimensions.maxHeight) {
        return {
          isValid: false,
          error: `Image height must not exceed ${config.dimensions.maxHeight}px`
        };
      }

      return {
        isValid: true,
        metadata: {
          dimensions: {
            width: dimensions.width,
            height: dimensions.height,
            aspectRatio: dimensions.width / dimensions.height
          }
        }
      };
    } catch (error) {
      return {
        isValid: false,
        error: 'Failed to validate image dimensions'
      };
    }
  }

  return {
    isValid: true
  };
}

async function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height
      });
    };
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    img.src = URL.createObjectURL(file);
  });
}

export type { ValidationResult };
